import { useState, useEffect, useCallback, useMemo } from "react";
import FilterMenu from "../../../components/FilterMenu";
import Loading from "../../../components/Loading";
import {
  ChartDataParams,
  FormatChartData,
} from "../../../components/recharts/functions";
import { ReMixChart } from "../../../components/recharts/ReMixCharts";
import { getGridcoIntraGen, getGridcoRegen } from "../../../Rest_api/RestApi";
import { R_CHART_TYPES } from "../const";
import { convertToString, getMonthFromDate } from "../../../extensions/date";
import { GenerationCommon } from "../../../models/gridco";
import { BrushStart } from "../../../models/chart_model";
import { SearchBox } from "../../../components/Search";
import LegendSummaryTable, {
  ChartTable,
  downloadChartTableCSV,
  downloadSummaryCSV,
} from "../../../components/table/SummaryTable";
import { renderHourTick } from "../../../components/recharts/components";
import { useSelector } from "react-redux";
import NewFilterContainer from "../../../components/NewFilterContainer ";
import MainPageContainer from "../../../components/MainPageContainer";
import SecondaryHeader from "../../Rajasthan/Demand/SecondaryHeader";
import Tooltip from "../../../components/tooltip";
import { stringToConsistentColor } from "../../../extensions/string";

const CHART_TYPES = [R_CHART_TYPES[0], R_CHART_TYPES[1], R_CHART_TYPES[2]];
const GROUP_BY = [
  { lable: "Ownership", value: "ownership" },
  { lable: "Generator Type", value: "generator_type" },
  { lable: "Station", value: "station_name" },
];
const UNITS = ["MW", "MWh"];
const TYPE = ["Entitlement", "Expp", "AdjustExpp", "Experi"];

const INTERVAL = [
  {
    label: "Time Slot",
    key: "time",
    name: "Time Slot",
    firstKey: "slot",
    secondKey: "date",
    values: ["slot", "date"],
    isTime: true,
  },
  {
    label: "Date",
    key: "date",
    name: "Date",
    firstKey: "date",
    secondKey: undefined,
    values: ["date"],
    isTime: false,
  },
  {
    label: "Month",
    key: "month",
    name: "Month",
    firstKey: "month",
    secondKey: undefined,
    values: ["month"],
    isTime: false,
  },
];

const chartSuffixes = [
  {
    suffix: "entitlement_value",
    displayName: "Entitlement",
    stackSuffix: "entitlement_value",
  },
  { suffix: "expp_value", displayName: "Expp", stackSuffix: "expp_value" },
  {
    suffix: "adj_expp_value",
    displayName: "AdjustExpp",
    stackSuffix: "adj_expp_value",
  },
  {
    suffix: "experi_value",
    displayName: "Experi",
    stackSuffix: "experi_value",
  },
];

export function GridcoGenrationIntrastate() {
  const [TableData, setTableData] = useState<any[]>([]);
  const dataTab = useSelector((state: any) => state.viewMode.activeView);
  const activeTab = useSelector((state: any) => state.switchTabs.activeTab);
  const DefaultDates = useSelector(
    (state: any) => state.defaultDatesSetter.defaultDates
  );
  const [data, setData] = useState<GenerationCommon[]>([]);
  const [selectedStation, setSelectedStation] = useState<any[]>([]);
  const [uniqueStations, setUniqueStations] = useState<string[]>([]);
  const [selectedSuffix, setSelectedSuffix] = useState([
    chartSuffixes[0],
    chartSuffixes[1],
    chartSuffixes[2],
  ]);
  const [chartData, setChartData] = useState<any[]>([]);
  const [chartKeys, setChartKeys] = useState<any[]>([]);
  const [selectedChartType, setSelectedChartType] = useState(CHART_TYPES[2]);
  const [selectedUnit, setSelectedUnit] = useState(UNITS[0]);
  const [uniqueBlock, setUniqueBlock] = useState<any[]>([]);
  const [selectedXaxis, setSelectedXaxis] = useState(INTERVAL[0]);
  const [selectedBlock, setSelectedBlock] = useState<any[]>([]);
  const [selectedGroupKeys, setSelectedGroupKeys] = useState(GROUP_BY[1].value);
  const [selectedGeneratorType, setSelectedGeneratorType] = useState<any[]>([]);
  const [genrators, setGenerators] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);

  const _handlefetchData = useCallback(async () => {
    if (DefaultDates[activeTab].defaultRangeDate === undefined) {
      return;
    }
    setLoading(true);
    const dates = DefaultDates[activeTab].defaultRangeDate;
    const RegenData = await getGridcoRegen({
      startDate: convertToString(dates.startDate),
      endDate: convertToString(dates.endDate),
    });

    const IntraData = await getGridcoIntraGen({
      startDate: convertToString(dates.startDate),
      endDate: convertToString(dates.endDate),
    });

    const newData: GenerationCommon[] = [...IntraData, ...RegenData];

    setUniqueStations(
      Array.from(
        new Set(newData.map((item) => item.station_name || item.sub_station))
      )
    );

    setData(newData);
    setUniqueBlock(Array.from(new Set(newData.map((item) => item.time_block))));
    setGenerators(
      Array.from(
        new Set(
          newData.map((item) => {
            return (
              item.generator_type.charAt(0).toUpperCase() +
              item.generator_type.slice(1).toLowerCase()
            );
          })
        )
      )
    );
    setLoading(false);
  }, [DefaultDates, activeTab]);

  function _handleSuffixChange(index: number) {
    // stack for 2 suffixes in
    if (selectedSuffix.length > 1) {
      setSelectedSuffix([chartSuffixes[index]]);
    } else {
      if (selectedSuffix.includes(chartSuffixes[index])) {
        return;
      }
      setSelectedSuffix([...selectedSuffix, chartSuffixes[index]]);
    }

    setSelectedSuffix((prev) =>
      prev.sort((a, b) => chartSuffixes.indexOf(a) - chartSuffixes.indexOf(b))
    );
  }

  useEffect(() => {
    // console.log("expo",data)
    const processedData = data.map((item) => ({
      ...item,
      adj_expp_value_mw: item.adj_expp_value || 0,
      expp_value_mw: item.expp_value || 0,
      experi_value_mw: item.experi_value || 0,
      entitlement_value_mw: item.entitlement || 0,
      adj_expp_value_mwh: (item.adj_expp_value || 0) / 4,
      expp_value_mwh: (item.expp_value || 0) / 4,
      experi_value_mwh: (item.experi_value || 0) / 4,
      entitlement_value_mwh: (item.entitlement || 0) / 4,
      month: getMonthFromDate(item.date),
      slot: item.time_block,
      generator_type:
        item.generator_type.charAt(0).toUpperCase() +
        item.generator_type.slice(1),
      station_name: item.station_name || item.sub_station,
    }));
    let filteredData = processedData;
    const dates = DefaultDates[activeTab].defaultRangeDate;
    filteredData = filteredData.filter(
      (item) =>
        new Date(item.date) >= dates.startDate &&
        new Date(item.date) <= dates.endDate
    );

    if (selectedBlock.length > 0) {
      filteredData = filteredData.filter((item) =>
        selectedBlock.map((item) => item.value).includes(item.slot)
      );
    }

    if (selectedGeneratorType.length > 0) {
      filteredData = filteredData.filter((item) => {
        return selectedGeneratorType
          .map((i) => i.label.toLowerCase())
          .includes(item.generator_type.toLowerCase());
      });
    }
    console.log(
      "filteredData",
      filteredData.filter((item) => item.station_name.includes("INFOCITY"))
    );

    setUniqueStations(
      Array.from(
        new Set(
          filteredData
            .filter((item) =>
              selectedGeneratorType.length === 0
                ? true
                : selectedGeneratorType
                    .map((i) => i.label.toLowerCase())
                    .includes(item.generator_type.toLowerCase())
            )
            .map((item) => item.station_name || item.sub_station)
        )
      )
    );

    if (selectedStation.length > 0) {
      filteredData = filteredData.filter((item) => {
        return selectedStation.map((i) => i.label).includes(item.station_name);
      });
    }

    const par: ChartDataParams = {
      keys: selectedXaxis.secondKey
        ? [selectedXaxis.firstKey, selectedXaxis.secondKey]
        : [selectedXaxis.firstKey],
      tableKeys: ["date", "slot"],
      groupBy: [selectedGroupKeys],
      volumeKey: "",
      aggregationKeys: [
        "adj_expp_value_mw",
        "entitlement_value_mw",
        "entitlement_value_mwh",
        "experi_value_mw",
        "experi_value_mwh",
        "expp_value_mw",
        "expp_value_mwh",
        "adj_expp_value_mwh",
      ],
      priceKey: "",
      data: filteredData,
      averageKeys: [
        "experi_value_mw",
        "expp_value_mw",
        "entitlement_value_mw",
        "adj_expp_value_mw",
      ],
      averageDuplicationKeys: ["date", "slot"],
      reverseSort: ["date", "slot"],
    };

    const cData = FormatChartData(par);
    setChartData(cData.data);
    setTableData(cData.tableData || []);
    setChartKeys(cData.keys);
  }, [
    DefaultDates,
    activeTab,
    data,
    selectedBlock,
    selectedGeneratorType,
    selectedGroupKeys,
    selectedStation,
    selectedXaxis,
  ]);

  useEffect(() => {
    _handlefetchData();
  }, [_handlefetchData]);

  function _handleGroupByChange(index: number) {
    setSelectedGroupKeys(GROUP_BY[index].value);
  }

  const handleKeyChange = (index: number, setSelectedUnit: any) => {
    setSelectedXaxis(INTERVAL[index]);
  };
  const handlexDataKey = useCallback(() => {
    if (selectedXaxis.key === "time") {
      if (selectedBlock.length > 0) {
        return "slot";
      }
      return "slot";
    } else {
      // if (selectedUnit !== DEMAND_UNITS[1]) {
      //   setSelectedUnit(DEMAND_UNITS[1]);
      // }
      return selectedXaxis.key;
    }
  }, [selectedBlock.length, selectedXaxis.key]);

  const xDataKey = useMemo(() => handlexDataKey(), [handlexDataKey]);

  const summaryExtraKeys = selectedXaxis.isTime
    ? [
        "slot", // Changed dateIndex to date
        selectedXaxis.values[1],
      ]
    : [selectedXaxis.values[0]];

  const summaryExtraLabels = selectedXaxis.values.map(
    (value) => (value === "date" ? "Date" : value) // Simplified label mapping
  );

  const createChartObject = (
    key: string,
    index: number,
    suffix: string,
    displayName: string,
    stackSuffix: string,
    selectedXaxis: { isTime: boolean },
    selectedChartType: { value: string; stackId?: string }
  ) => {
    return {
      dataKey: `${key}_${suffix}_${selectedUnit.toLowerCase()}`,
      name: key + " " + displayName,
      legendColor: undefined,
      stroke: stringToConsistentColor(key + " " + displayName),
      type: selectedChartType.value,
      stackId:
        selectedChartType.stackId !== undefined ? stackSuffix : undefined,
    };
  };

  function getLegends() {
    return selectedSuffix.flatMap(({ suffix, displayName, stackSuffix }) =>
      chartKeys.map(
        (key, index) =>
          createChartObject(
            key,
            index,
            suffix,
            displayName,
            stackSuffix,
            selectedXaxis,
            selectedChartType as any
          ) as any
      )
    );
  }

  const tablePar = {
    legends: getLegends(),
    data: chartData,
    tableData: TableData,
    itemsPerPage: 96,
    unit: selectedUnit,
    extraKeys: summaryExtraKeys,
    extraLabels: summaryExtraLabels,
    filename: "Generation By Intrastate",
  };

  return (
    <div className="gas-page-container">
      {loading && <Loading />}
      <NewFilterContainer>
        <div className="side-filters-container text-nowrap">
          <FilterMenu
            title="Interval"
            items={INTERVAL.map((item) => item.name)}
            selectedItems={[selectedXaxis.name]}
            onSelect={(item, index) => {
              handleKeyChange(index, setSelectedUnit);
            }}
          />

          <FilterMenu
            title="Unit"
            items={UNITS}
            selectedItems={[selectedUnit]}
            onSelect={(item, index) => {
              setSelectedUnit(item);
            }}
          />

          <FilterMenu
            title="Group By"
            items={GROUP_BY.map((item) => item.lable)}
            selectedItems={[
              GROUP_BY.find((g) => g.value === selectedGroupKeys)?.lable || "",
            ]}
            onSelect={(item, index) => {
              _handleGroupByChange(index);
              setSelectedGeneratorType([]);
            }}
          />

          <FilterMenu
            title="Variable"
            items={TYPE}
            selectedItems={selectedSuffix.map((item) => item.displayName)}
            setSelectedItems={(selectedDisplayNames: string[]) => {
              setSelectedSuffix(
                chartSuffixes.filter((suffixObj) =>
                  selectedDisplayNames.includes(suffixObj.displayName)
                )
              );
            }}
          />

          <FilterMenu
            title="Chart Type"
            items={CHART_TYPES.map((item) => item.label)}
            selectedItems={[selectedChartType.label]}
            onSelect={(item, index) => {
              setSelectedChartType(CHART_TYPES[index]);
            }}
          />
        </div>
      </NewFilterContainer>

      <MainPageContainer>
        <SecondaryHeader />
        <div className="gas-chart-container">
          <div className="gas-heading">
            <h2 className="chartHeading w-80">Generation Intrastate</h2>
            <div className="flex gap-x-2 w-full justify-end">
              <SearchBox
                options={uniqueBlock.map((item) => {
                  return { value: item, label: item };
                })}
                isMany={true}
                placeholder="Search Block"
                selectedOptions={selectedBlock}
                onChange={setSelectedBlock}
              />

              <SearchBox
                options={uniqueStations.map((item) => {
                  return { value: item, label: item };
                })}
                isMany={true}
                placeholder="Search Station"
                selectedOptions={selectedStation}
                onChange={setSelectedStation}
              />

              {selectedGroupKeys === "station_name" && (
                <SearchBox
                  options={genrators.map((item) => {
                    return { value: item, label: item };
                  })}
                  isMany={true}
                  placeholder="Search Generator Type"
                  selectedOptions={selectedGeneratorType}
                  onChange={setSelectedGeneratorType}
                />
              )}

              <button
                onClick={() => {
                  dataTab.label === "Chart" && downloadChartTableCSV(tablePar);
                  //

                  dataTab.label === "Summary" && downloadSummaryCSV(tablePar);

                  dataTab.label === "Table" && downloadChartTableCSV(tablePar);
                }}
                className="downloadButton"
              >
                Download Data
              </button>
            </div>
          </div>

          {dataTab.label === "Chart" && (
            <ReMixChart
              data={chartData}
              showBrush={chartData.length > 96 * 7 ? true : undefined}
              brushStart={BrushStart.Start}
              isTimeSlot={selectedXaxis.firstKey === "slot"}
              onlyTitle={true}
              unit={selectedUnit}
              yAxisLabel={selectedUnit}
              legends={getLegends()}
              xDataKey={xDataKey}
              xTick={
                selectedXaxis.isTime && selectedBlock.length === 0
                  ? (value: any, index: any) => {
                      return renderHourTick(value, 10);
                    }
                  : undefined
              }
              secondXDataKey={
                //   "date"
                selectedXaxis.secondKey
              }
            />
          )}
          {dataTab.label === "Summary" && <LegendSummaryTable {...tablePar} />}
          {dataTab.label === "Table" && <ChartTable {...tablePar} />}
        </div>
        <div className="gna-footer-container">
          <div className="grid grid-cols-3 justify-between gap-5 ">
            <Tooltip
              note={
                <p>
                  1. SLDC: Daily CGP scheduling <br /> 2. ABT Meter readings (W
                  exp) : Solar/SHEP/Biomass. <br /> 3. Gridco : Vedanta IPP,
                  OPGC Stage II, State Hydro.
                </p>
              }
              text="1. OPGC Stage I taken from state hydro file (IB Unit I & II).
              2. W exp mapped from Annexure 4 is considered genration.
              3. Solar/SHEP/Biomass has been named mapped.
              4. OPGC Stage II Entitlement is taken from Declared Capacity.
              5. Hydro Entitlement is same as Expp.
              6. Vedanta Entitlement is taken from Total Injection (considering backdown).
"
            >
              <p className="gna-footer-text">Source</p>
            </Tooltip>
            <p className="gna-footer-text col-span-1 text-center">
              Powered by <strong>GNA Energy</strong>
            </p>
            <strong className="gna-footer-text col-span-1 text-end">
              Beta Version
            </strong>
          </div>
        </div>
      </MainPageContainer>
    </div>
  );
}
